// PDF de pedido de compra pra fornecedor terceirizado.
// Gera A4 com:
//   • Header: identidade MINI + título do tipo + período do filtro + data
//   • Resumo: contagem agregada por dimensão (quando aplicável)
//   • Tabela detalhada: nº pedido, cliente, prazo, móvel, medida
//
// Uso: gerarPdfTerceirizado(tipoId, pedidos, { filtroLabel })

import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { labelTerceirizado, temDimensao, moveisQueUsam } from '../data/terceirizados'

function fmtData(d) {
  if (!d || d === '0000-00-00') return '—'
  try { return format(new Date(d + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR }) } catch { return d }
}

// Agrupa contagem de móveis por dimensão "CxP cm" (pra resumo)
function agruparPorDimensao(pedidos, tipoId) {
  const map = new Map()
  for (const p of pedidos) {
    for (const m of moveisQueUsam(p, tipoId)) {
      const chave = m.comprimento && m.profundidade
        ? `${m.comprimento}×${m.profundidade} cm`
        : 'sem medida'
      map.set(chave, (map.get(chave) || 0) + 1)
    }
  }
  // Ordena por quantidade desc
  return [...map.entries()].sort((a, b) => b[1] - a[1])
}

// Conta total de itens necessários (cada móvel = 1 item)
function totalItens(pedidos, tipoId) {
  return pedidos.reduce((s, p) => s + moveisQueUsam(p, tipoId).length, 0)
}

export function gerarPdfTerceirizado(tipoId, pedidos, { filtroLabel = 'Todos' } = {}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const tipoNome = labelTerceirizado(tipoId).toUpperCase()
  const totalQtd = totalItens(pedidos, tipoId)

  // ── Header amarelo MINI ──────────────────────────────────────────────────
  doc.setFillColor(245, 194, 0)
  doc.rect(0, 0, pageW, 18, 'F')
  doc.setFontSize(14).setFont('helvetica', 'bold').setTextColor(17, 17, 17)
  doc.text('MINI MARCENARIA', 10, 11)
  doc.setFontSize(9).setFont('helvetica', 'normal')
  doc.text(`Pedido de Compra · ${tipoNome}`, pageW - 10, 11, { align: 'right' })

  // ── Subheader: data + filtro ─────────────────────────────────────────────
  doc.setFontSize(8).setTextColor(80)
  const hoje = format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
  doc.text(`Emitido em ${hoje}  ·  Prazo: ${filtroLabel}  ·  ${totalQtd} item${totalQtd !== 1 ? 's' : ''}`, 10, 25)

  // ── Resumo agrupado por dimensão (só pra base/metalon) ───────────────────
  let yCursor = 33
  if (temDimensao(tipoId)) {
    const grupos = agruparPorDimensao(pedidos, tipoId)
    if (grupos.length > 0) {
      doc.setFontSize(10).setFont('helvetica', 'bold').setTextColor(17, 17, 17)
      doc.text('Resumo por medida:', 10, yCursor)
      yCursor += 5
      doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(40)
      grupos.forEach(([medida, qtd], i) => {
        const col = i % 3
        const row = Math.floor(i / 3)
        const x   = 12 + col * 65
        const y   = yCursor + row * 5
        doc.text(`•  ${qtd}× ${medida}`, x, y)
      })
      yCursor += Math.ceil(grupos.length / 3) * 5 + 4
    }
  } else {
    // Pra pé palito: só total
    doc.setFontSize(10).setFont('helvetica', 'bold').setTextColor(17, 17, 17)
    doc.text(`Quantidade total: ${totalQtd}  ·  ${pedidos.length} pedido${pedidos.length !== 1 ? 's' : ''}`, 10, yCursor)
    yCursor += 6
  }

  // ── Tabela detalhada ─────────────────────────────────────────────────────
  const rows = []
  pedidos.forEach(p => {
    const moveis = moveisQueUsam(p, tipoId)
    moveis.forEach((m, idx) => {
      rows.push([
        idx === 0 ? (p.numero || '—')             : '',
        idx === 0 ? (p.cliente?.nome || '—')      : '',
        idx === 0 ? fmtData(p.prazo)              : '',
        m.modelo || '—',
        m.comprimento && m.profundidade
          ? `${m.comprimento}×${m.profundidade} cm`
          : '—',
      ])
    })
  })

  autoTable(doc, {
    startY: yCursor + 2,
    head: [['Pedido', 'Cliente', 'Prazo', 'Móvel', 'Medida']],
    body: rows,
    theme: 'grid',
    styles:     { fontSize: 8, cellPadding: 1.8, valign: 'middle' },
    headStyles: { fillColor: [17, 17, 17], textColor: 245, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 22, font: 'courier', fontStyle: 'bold' },
      1: { cellWidth: 50 },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 'auto' },
      4: { cellWidth: 28, halign: 'center', font: 'courier', fontStyle: 'bold' },
    },
    didDrawPage: (data) => {
      // Rodapé com número da página
      const pageH = doc.internal.pageSize.getHeight()
      const pageNum = doc.getNumberOfPages()
      doc.setFontSize(7).setTextColor(120)
      doc.text(`Página ${data.pageNumber} de ${pageNum}`, pageW - 10, pageH - 6, { align: 'right' })
      doc.text('mini.deck · Compra de terceirizado', 10, pageH - 6)
    },
  })

  // ── Salva ────────────────────────────────────────────────────────────────
  const dataArquivo = format(new Date(), 'yyyy-MM-dd', { locale: ptBR })
  doc.save(`compra-${tipoId}-${dataArquivo}.pdf`)
}
